import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, User, Clock, Key, Plus, ClipboardList, Pencil, Trash2, CheckCircle, XCircle } from 'lucide-react';
import UserModal from './UserModal';
import UserDetail from './UserDetail';
import TabPermissionsModal from './TabPermissionsModal';
import './UserManagement.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [permsUser, setPermsUser] = useState(null);
  const [actionsPour, setActionsPour] = useState(null);
  const [userStats, setUserStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [generatingAccounts, setGeneratingAccounts] = useState(false); // Génération des comptes clients

  // États pour gestion équipe chef d'équipe
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedChef, setSelectedChef] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [teamAgents, setTeamAgents] = useState([]);
  const [assignData, setAssignData] = useState({ agent_id: '', commission_attribuee: '0' });
  const [teamError, setTeamError] = useState('');
  const [chefStats, setChefStats] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);

      // Fetch stats for each user
      await fetchAllUserStats(response.data, token);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUserStats = async (usersList, token) => {
    try {
      const statsPromises = usersList.map(user =>
        axios.get(`/api/users/${user.id}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      const statsResponses = await Promise.all(statsPromises);
      const newStats = {};

      usersList.forEach((user, index) => {
        newStats[user.id] = statsResponses[index].data;
      });

      setUserStats(newStats);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setShowDetail(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowModal(true);
    setShowDetail(false);
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const handleSaveUser = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, userData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/users', userData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      throw error;
    }
  };

  const handleGenerateClientAccounts = async () => {
    if (!window.confirm('Voulez-vous générer automatiquement des comptes pour tous les clients e-shop qui n\'en ont pas encore (payés ou non) ?')) {
      return;
    }

    setGeneratingAccounts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/admin/generate-client-accounts', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { results } = response.data;

      let message = `✅ Génération terminée!\n\n`;
      message += `✓ ${results.success.length} comptes créés avec succès\n`;
      if (results.errors.length > 0) {
        message += `✗ ${results.errors.length} erreurs\n\n`;
      }

      if (results.success.length > 0) {
        message += `\nIdentifiants créés:\n`;
        results.success.forEach((account, index) => {
          if (index < 10) { // Afficher les 10 premiers
            message += `\n${account.clientName}:\n`;
            message += `  Login: ${account.username}\n`;
            message += `  Mot de passe: ${account.password}\n`;
          }
        });
        if (results.success.length > 10) {
          message += `\n... et ${results.success.length - 10} autres`;
        }
      }

      alert(message);

      // Rafraîchir les utilisateurs
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la génération des comptes:', error);
      alert('❌ Erreur lors de la génération des comptes: ' + (error.response?.data?.error || error.message));
    } finally {
      setGeneratingAccounts(false);
    }
  };

  // Fonctions pour gestion équipe chef d'équipe
  const handleManageTeam = async (chef) => {
    setSelectedChef(chef);
    setTeamError('');
    setAssignData({ agent_id: '', commission_attribuee: '0' });
    try {
      const token = localStorage.getItem('token');
      const [agentsRes, availableRes, statsRes] = await Promise.all([
        axios.get(`/api/chefs-equipe/${chef.id}/agents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`/api/chefs-equipe/${chef.id}/agents-disponibles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`/api/chefs-equipe/${chef.id}/commission-stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTeamAgents(agentsRes.data);
      setAvailableAgents(availableRes.data);
      setChefStats(statsRes.data);
      setShowTeamModal(true);
    } catch (error) {
      console.error('Erreur chargement équipe:', error);
      alert('Erreur lors du chargement de l\'équipe');
    }
  };

  const handleAssignAgent = async (e) => {
    e.preventDefault();
    setTeamError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/chefs-equipe/${selectedChef.id}/assign-agent`, assignData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Rafraîchir les données
      handleManageTeam(selectedChef);
      setAssignData({ agent_id: '', commission_attribuee: '0' });
    } catch (error) {
      setTeamError(error.response?.data?.error || 'Erreur lors de l\'assignation');
    }
  };

  const handleRemoveFromTeam = async (agentId) => {
    if (!window.confirm('Retirer cet agent de l\'équipe ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/chefs-equipe/${selectedChef.id}/remove-agent/${agentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      handleManageTeam(selectedChef);
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors du retrait');
    }
  };

  const handleUpdateAgentCommission = async (agentId, newCommission) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/chefs-equipe/agents/${agentId}/commission`, {
        commission_attribuee: newCommission
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      handleManageTeam(selectedChef);
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const actionModaleStyle = (couleur) => ({
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '13px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
    background: couleur, color: '#fff', fontSize: 14.5, fontWeight: 600, textAlign: 'left'
  });

  const getRoleBadge = (role) => {
    const badges = {
      admin: { text: 'Administrateur', class: 'badge-admin' },
      agent: { text: 'Agent Téléphonique', class: 'badge-agent' },
      commercial: { text: 'Directeur Commercial', class: 'badge-commercial' },
      chef_equipe: { text: 'Chef d\'équipe', class: 'badge-chef-equipe' },
      sous_admin: { text: 'Sous-Admin', class: 'badge-chef-equipe' },
      superviseur: { text: 'Superviseur', class: 'badge-superviseur' },
      superviseur_agent: { text: 'Superviseur + Agent', class: 'badge-superviseur-agent' },
      client: { text: 'Client', class: 'badge-client' },
      valideur: { text: 'Valideur de comptes', class: 'badge-chef-equipe' },
      rh: { text: 'RH — Candidat Assu', class: 'badge-chef-equipe' },
      suivi_connect: { text: 'Suivi E-SHOPS CONNECT', class: 'badge-chef-equipe' }
    };
    return badges[role] || { text: role, class: 'badge-default' };
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ' €';
  };

  // Filtrer les utilisateurs équipe (admin, agent, commercial, chef_equipe, superviseur, superviseur_agent)
  const teamUsers = users.filter(user => {
    const isTeam = ['admin', 'agent', 'commercial', 'chef_equipe', 'sous_admin', 'superviseur', 'superviseur_agent', 'valideur', 'rh'].includes(user.role);
    const matchSearch = !searchTerm || (() => {
      const search = searchTerm.toLowerCase();
      return (
        user.nom?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.role?.toLowerCase().includes(search)
      );
    })();
    return isTeam && matchSearch;
  });

  // Filtrer les clients
  const clientUsers = users.filter(user => {
    const isClient = user.role === 'client';
    const matchSearch = !searchTerm || (() => {
      const search = searchTerm.toLowerCase();
      return (
        user.nom?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search)
      );
    })();
    return isClient && matchSearch;
  });

  return (
    <main className="dashboard-main">
      <div className="dashboard-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Rechercher un utilisateur (nom, email, rôle)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button onClick={handleAddUser} className="btn-add">
          + Nouvel utilisateur
        </button>
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Utilisateurs</h3>
          <p className="stat-number">{users.length}</p>
        </div>
        <div className="stat-card">
          <h3>Administrateurs</h3>
          <p className="stat-number">{users.filter(u => u.role === 'admin').length}</p>
        </div>
        <div className="stat-card">
          <h3>Superviseurs</h3>
          <p className="stat-number">{users.filter(u => u.role === 'superviseur' || u.role === 'superviseur_agent').length}</p>
        </div>
        <div className="stat-card">
          <h3>Agents</h3>
          <p className="stat-number">{users.filter(u => u.role === 'agent' || u.role === 'superviseur_agent').length}</p>
        </div>
        <div className="stat-card">
          <h3>Commerciaux</h3>
          <p className="stat-number">{users.filter(u => u.role === 'commercial').length}</p>
        </div>
        <div className="stat-card">
          <h3>Clients</h3>
          <p className="stat-number">{users.filter(u => u.role === 'client').length}</p>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Chargement des utilisateurs...</div>
      ) : (
        <>
          {/* TABLEAU ÉQUIPE */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '20px',
              color: '#333',
              borderBottom: '3px solid #667eea',
              paddingBottom: '10px'
            }}>
              <Users size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Équipe (Admin, Agents, Commerciaux)
            </h2>
            {teamUsers.length === 0 ? (
              <div className="empty-state">
                <p>{searchTerm ? 'Aucun membre d\'équipe ne correspond à votre recherche' : 'Aucun membre d\'équipe trouvé'}</p>
              </div>
            ) : (
              <div className="users-table users-table-fit">
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Rôle</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamUsers.map((user) => {
                      return (
                        <tr key={user.id}>
                          <td>
                            <button
                              onClick={() => handleViewUser(user)}
                              className="btn-view"
                            >
                              Voir détail
                            </button>
                          </td>
                          <td style={{fontWeight: 600}}>{user.nom}</td>
                          <td>{user.email}</td>
                          <td>
                            <span className={`role-badge ${getRoleBadge(user.role).class}`}>
                              {user.role === 'agent' && user.is_agent_cee ? 'Agent Téléphonique CEE'
                                : user.role === 'agent' && user.is_agent_pacpv ? 'Agent Téléphonique PAC/PV'
                                : user.role === 'chef_equipe' && user.cree_agents_cee ? 'Chef d\'équipe CEE'
                                : user.role === 'chef_equipe' && user.cree_agents_pacpv ? 'Chef d\'équipe PAC/PV'
                                : getRoleBadge(user.role).text}
                            </span>
                            {(() => {
                              const pole = (user.is_agent_cee || user.cree_agents_cee) ? 'CEE'
                                : (user.is_agent_pacpv || user.cree_agents_pacpv) ? 'PAC/PV'
                                : (['agent','chef_equipe','commercial'].includes(user.role) ? 'E-Shops' : null);
                              if (!pole) return null;
                              const bg = pole === 'CEE' ? '#7c3aed' : pole === 'PAC/PV' ? '#0a8a4a' : '#2563eb';
                              return <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#fff', background: bg, borderRadius: 10, padding: '2px 7px', verticalAlign: 'middle' }}>{pole}</span>;
                            })()}
                          </td>
                          <td className="actions">
                            <button
                              onClick={() => setActionsPour(user)}
                              className="btn-actions-menu"
                              title="Actions sur ce compte"
                            >
                              <Pencil size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* TABLEAU CLIENTS */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#333',
                borderBottom: '3px solid #3498db',
                paddingBottom: '10px',
                margin: 0
              }}>
                <User size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Clients
              </h2>
              <button
                onClick={handleGenerateClientAccounts}
                disabled={generatingAccounts}
                className="btn-add"
                style={{
                  background: generatingAccounts ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  cursor: generatingAccounts ? 'not-allowed' : 'pointer'
                }}
              >
                {generatingAccounts ? <><Clock size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Génération...</> : <><Key size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Générer Comptes Clients</>}
              </button>
            </div>
            {clientUsers.length === 0 ? (
              <div className="empty-state">
                <p>{searchTerm ? 'Aucun client ne correspond à votre recherche' : 'Aucun client trouvé'}</p>
              </div>
            ) : (
              <div className="users-table users-table-fit">
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientUsers.map((user) => {
                      return (
                        <tr key={user.id}>
                          <td>
                            <button
                              onClick={() => handleViewUser(user)}
                              className="btn-view"
                            >
                              Voir détail
                            </button>
                          </td>
                          <td style={{fontWeight: 600}}>{user.nom}</td>
                          <td>{user.email}</td>
                          <td>
                            {user.client_login ? (
                              <strong style={{ fontFamily: 'monospace', color: '#667eea', fontSize: '14px' }}>
                                {user.client_login}
                              </strong>
                            ) : (
                              <span style={{ color: '#999' }}>-</span>
                            )}
                          </td>
                          <td className="actions">
                            <button
                              onClick={() => setActionsPour(user)}
                              className="btn-actions-menu"
                              title="Actions sur ce compte"
                            >
                              <Pencil size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showModal && (
        <UserModal
          user={editingUser}
          onClose={() => setShowModal(false)}
          onSave={handleSaveUser}
        />
      )}

      {showDetail && viewingUser && (
        <UserDetail
          user={viewingUser}
          stats={userStats[viewingUser.id]}
          onClose={() => setShowDetail(false)}
          onUpdate={(user) => {
            setShowDetail(false);
            handleEditUser(user);
          }}
        />
      )}

      {/* Fenêtre d'actions : regroupe tout ce qu'on peut faire sur un compte,
          ce qui libère la largeur du tableau au lieu d'aligner quatre boutons. */}
      {actionsPour && (
        <div
          onClick={() => setActionsPour(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
          }}>
            <div style={{ marginBottom: 6, fontSize: 18, fontWeight: 800, color: '#2f3648' }}>
              {actionsPour.nom}
            </div>
            <div style={{ fontSize: 13, color: '#5f6b85', marginBottom: 20 }}>
              {actionsPour.email} · {getRoleBadge(actionsPour.role).text}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <button
                onClick={() => { const u = actionsPour; setActionsPour(null); handleViewUser(u); }}
                style={actionModaleStyle('#6b3fa0')}
              >
                <ClipboardList size={16} /> Voir le détail
              </button>

              {(actionsPour.role === 'chef_equipe' || actionsPour.role === 'sous_admin') && (
                <button
                  onClick={() => { const u = actionsPour; setActionsPour(null); handleManageTeam(u); }}
                  style={actionModaleStyle('#11998e')}
                >
                  <Users size={16} /> Gérer l'équipe
                </button>
              )}

              {actionsPour.role !== 'admin' && actionsPour.role !== 'client' && (
                <button
                  onClick={() => { const u = actionsPour; setActionsPour(null); setPermsUser(u); }}
                  style={actionModaleStyle('#7b2ff7')}
                >
                  <Key size={16} /> Autorisations — onglets visibles
                </button>
              )}

              <button
                onClick={() => { const u = actionsPour; setActionsPour(null); handleEditUser(u); }}
                style={actionModaleStyle('#2f80ed')}
              >
                <Pencil size={16} /> Modifier le compte
              </button>

              <button
                onClick={() => { const u = actionsPour; setActionsPour(null); handleDeleteUser(u.id); }}
                style={actionModaleStyle('#e04b3e')}
              >
                <Trash2 size={16} /> Supprimer le compte
              </button>
            </div>

            <button
              onClick={() => setActionsPour(null)}
              style={{
                width: '100%', marginTop: 18, padding: '11px 0', borderRadius: 9,
                border: '1.5px solid #ddd', background: '#fff', color: '#5f6b85',
                fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {permsUser && (
        <TabPermissionsModal
          user={permsUser}
          onClose={() => setPermsUser(null)}
          onSaved={(userId, hidden) => {
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, hidden_tabs: JSON.stringify(hidden) } : u));
          }}
        />
      )}

      {/* Modal Gestion Équipe Chef d'équipe */}
      {showTeamModal && selectedChef && (
        <div className="modal-overlay">
          <div className="modal-content team-modal" style={{ maxWidth: '800px', width: '95%' }}>
            <div className="modal-header">
              <h3><Users size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Équipe de {selectedChef.nom}</h3>
              <button className="btn-close" onClick={() => setShowTeamModal(false)}>&times;</button>
            </div>

            {/* Stats Commission */}
            {chefStats && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
                <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '15px', borderRadius: '10px', color: 'white', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Clients payés × {chefStats.commission_par_client?.toFixed(0) || 0}€</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>{chefStats.clients_payes || 0} = {chefStats.commissions_gagnees?.toFixed(2) || '0.00'} €</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)', padding: '15px', borderRadius: '10px', color: 'white', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Total Attribué</div>
                  <div style={{ fontSize: '20px', fontWeight: '700' }}>{chefStats.total_attribue.toFixed(2)} €</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)', padding: '15px', borderRadius: '10px', color: 'white', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Disponible</div>
                  <div style={{ fontSize: '20px', fontWeight: '700' }}>{chefStats.disponible.toFixed(2)} €</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)', padding: '15px', borderRadius: '10px', color: 'white', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Agents</div>
                  <div style={{ fontSize: '20px', fontWeight: '700' }}>{chefStats.nombre_agents}</div>
                </div>
              </div>
            )}

            {/* Assigner un agent */}
            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '10px', marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 15px', color: '#333' }}><Plus size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Assigner un agent existant</h4>
              {availableAgents.length === 0 ? (
                <p style={{ color: '#666', margin: 0 }}>Aucun agent disponible à assigner.</p>
              ) : (
                <form onSubmit={handleAssignAgent} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 2, minWidth: '200px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600' }}>Agent</label>
                    <select
                      value={assignData.agent_id}
                      onChange={e => setAssignData({ ...assignData, agent_id: e.target.value })}
                      required
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      <option value="">-- Sélectionner --</option>
                      {availableAgents.map(agent => (
                        <option key={agent.id} value={agent.id}>
                          {agent.nom} ({agent.role})
                          {parseFloat(agent.commission_fixe || 0) > 0 ? ` [Admin: ${agent.commission_fixe}€]` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: '120px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600' }}>Commission Chef (€)</label>
                    <input
                      type="number"
                      value={assignData.commission_attribuee}
                      onChange={e => setAssignData({ ...assignData, commission_attribuee: e.target.value })}
                      min="0"
                      step="0.01"
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <button
                    type="submit"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Assigner
                  </button>
                </form>
              )}
              {teamError && <p style={{ color: '#e74c3c', margin: '10px 0 0', fontSize: '14px' }}>{teamError}</p>}
            </div>

            {/* Liste des agents de l'équipe */}
            <div>
              <h4 style={{ margin: '0 0 15px', color: '#333' }}><ClipboardList size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Agents de l'équipe ({teamAgents.length})</h4>
              {teamAgents.length === 0 ? (
                <p style={{ color: '#666', textAlign: 'center', padding: '30px' }}>Aucun agent dans cette équipe.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f0f0f0' }}>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Nom</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px' }}>Email</th>
                      <th style={{ padding: '10px', textAlign: 'center', fontSize: '13px' }}>Com. Admin</th>
                      <th style={{ padding: '10px', textAlign: 'center', fontSize: '13px' }}>Com. Chef</th>
                      <th style={{ padding: '10px', textAlign: 'center', fontSize: '13px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamAgents.map(agent => (
                      <tr key={agent.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px 10px', fontWeight: '600' }}>{agent.nom}</td>
                        <td style={{ padding: '12px 10px', color: '#666', fontSize: '14px' }}>{agent.email}</td>
                        <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                          {parseFloat(agent.commission_fixe || 0) > 0 ? (
                            <span style={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              {parseFloat(agent.commission_fixe).toFixed(0)} €
                            </span>
                          ) : '-'}
                        </td>
                        <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                          <input
                            type="number"
                            value={agent.commission_attribuee || 0}
                            onChange={e => {
                              const newAgents = teamAgents.map(a =>
                                a.id === agent.id ? { ...a, commission_attribuee: e.target.value } : a
                              );
                              setTeamAgents(newAgents);
                            }}
                            onBlur={e => handleUpdateAgentCommission(agent.id, e.target.value)}
                            min="0"
                            step="0.01"
                            style={{
                              width: '80px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '2px solid #e67e22',
                              textAlign: 'center',
                              fontWeight: '600'
                            }}
                          />
                        </td>
                        <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleRemoveFromTeam(agent.id)}
                            style={{
                              background: '#ffebee',
                              color: '#c62828',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setShowTeamModal(false)}
                style={{
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default UserManagement;
